// Hook: useTreeAuthoring.js
// Purpose: Compose existing Strategy Builder creation, wiring, operand and selection commands for Tree controls.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { firstFreeTreeSlot } from './useTreeInteraction';
import { LINK_REFUSAL_TEXT } from './useGraphWiring';
import { COMPARATOR_OPS } from '../utils/nodeUtils';
import {
  resolveSourceFields,
  resolveTargetHandle,
  setOperandConstant,
} from '../utils/operandLinking';

const itemIdentity = (item) => ({
  indicator: item?.data?.indicator,
  action: item?.data?.action,
  panel: item?.data?.panel,
  operator: item?.data?.operator,
});

const sameIdentity = (candidate, requested) => {
  if (!candidate || !requested || candidate.type !== requested.type) return false;
  const requestedIdentity = itemIdentity(requested);
  const entries = Object.entries(requestedIdentity).filter(([, value]) => value !== undefined);
  if (entries.length === 0) return true;
  return entries.every(([key, value]) => itemIdentity(candidate)[key] === value);
};

const findCatalogueItem = (requested, catalogueItems) => {
  if (!requested) return null;
  return catalogueItems.find(({ id }) => requested.id && id === requested.id)
    || catalogueItems.find((candidate) => sameIdentity(candidate, requested))
    || null;
};

const targetHandleFor = (targetNode, inputIndex, explicitHandle) => (
  resolveTargetHandle(targetNode, explicitHandle, inputIndex)
);

const refusal = (reason) => ({ ok: false, reason });

// Tree authoring creates exactly the requested node. The factory still owns its default chart-pane
// behavior for every existing caller; this explicit option suppresses only the extra pane side effect.
export const TREE_AUTHORING_FACTORY_OPTIONS = Object.freeze({ autoCreateChartPane: false });

export default function useTreeAuthoring({
  nodes = [],
  edges = [],
  graphWiring,
  createNode,
  updateNodeData,
  setSelectedNode,
  catalogueItems = [],
  availableDataFields,
  allIndicatorMetadata,
  stagingActive = true,
}) {
  const activationLatch = useRef(new Set());
  const graphWiringRef = useRef(graphWiring);
  const stagedOperandRef = useRef(null);
  const [stagedOperand, setStagedOperand] = useState(null);

  useEffect(() => {
    graphWiringRef.current = graphWiring;
  }, [graphWiring]);

  const clearStagedOperand = useCallback((nodeId) => {
    if (stagedOperandRef.current?.id !== nodeId) return;
    stagedOperandRef.current = null;
    setStagedOperand(null);
  }, []);

  const rollbackStagedOperand = useCallback((updateState = true) => {
    const staged = stagedOperandRef.current;
    if (!staged) return;
    stagedOperandRef.current = null;
    if (updateState) setStagedOperand(null);
    graphWiringRef.current?.deleteNode?.(staged.id, { createdEdges: staged.createdEdges });
  }, []);

  useEffect(() => {
    if (stagedOperand && !stagingActive) rollbackStagedOperand();
  }, [rollbackStagedOperand, stagedOperand, stagingActive]);

  useEffect(() => () => rollbackStagedOperand(false), [rollbackStagedOperand]);

  const operatorOptions = useMemo(() => Object.entries(COMPARATOR_OPS).map(([id, config]) => ({
    id,
    label: config.label,
    glyph: config.glyph,
    value: id,
  })), []);

  const releaseLatch = useCallback((key) => {
    Promise.resolve().then(() => activationLatch.current.delete(key));
  }, []);

  const createAndAttach = useCallback(({ item: requestedItem, target = {} }) => {
    const item = findCatalogueItem(requestedItem, catalogueItems);
    if (!item) return refusal(LINK_REFUSAL_TEXT.missing_item);
    const targetNode = target.node
      || nodes.find(({ id }) => id === (target.nodeId || target.target));
    if ((target.nodeId || target.target) && !targetNode) {
      return refusal(LINK_REFUSAL_TEXT.missing_node);
    }
    const requestedTargetHandle = target.targetHandle;
    const hasRequestedTargetHandle = requestedTargetHandle !== undefined
      && requestedTargetHandle !== null
      && requestedTargetHandle !== '';
    const freeTargetHandle = targetNode ? firstFreeTreeSlot(targetNode, edges) : null;
    const resolvedRequestedHandle = targetNode && hasRequestedTargetHandle
      ? resolveTargetHandle(targetNode, requestedTargetHandle)
      : null;
    // AND/OR growth returns the next valid handle before that slot exists in node.data.inputs.
    // Accept only that authority-produced dynamic handle; arbitrary unknown handles stay refused.
    const targetHandle = targetNode
      ? (hasRequestedTargetHandle
        ? (resolvedRequestedHandle
          ?? (String(requestedTargetHandle) === String(freeTargetHandle) ? freeTargetHandle : null))
        : freeTargetHandle)
      : null;
    if (targetNode && !targetHandle) return refusal(LINK_REFUSAL_TEXT.no_free_slot);

    const latchKey = `${targetNode?.id || target.sectionId || 'root'}:${item.id}`;
    if (activationLatch.current.has(latchKey)) return { ok: false, reason: '', duplicate: true };
    activationLatch.current.add(latchKey);
    releaseLatch(latchKey);

    if (targetNode) {
      const provisionalNode = { id: '__provisional', type: item.type, data: item.data };
      const validation = graphWiring.validateConnection({
        source: provisionalNode.id,
        sourceHandle: item.sourceHandle || 'value',
        target: targetNode.id,
        targetHandle,
      }, {
        nodesSnapshot: [...nodes, provisionalNode],
        edgesSnapshot: [...edges],
      });
      if (!validation.valid) return refusal(validation.reason);
    }

    const created = createNode(item.type, null, { ...(item.data || {}) }, TREE_AUTHORING_FACTORY_OPTIONS);
    if (!created?.success || !created.node) {
      return refusal(created?.message || LINK_REFUSAL_TEXT.node_creation_failed);
    }

    if (targetNode) {
      const outcome = graphWiring.onConnect({
        source: created.node.id,
        sourceHandle: item.sourceHandle || 'value',
        target: targetNode.id,
        targetHandle,
      }, {
        nodesSnapshot: [...nodes, created.node],
        edgesSnapshot: [...edges, ...(created.createdEdges || [])],
      });
      if (!outcome?.ok) {
        // Defensive guard only: validation above prevents normal refusals before factory side effects.
        graphWiring.deleteNode(created.node.id, { createdEdges: created.createdEdges || [] });
        return refusal(outcome?.reason || LINK_REFUSAL_TEXT.connection_not_allowed);
      }
      setSelectedNode?.(created.node);
      return { ok: true, reason: '', node: created.node, params: outcome.params };
    }

    setSelectedNode?.(created.node);
    return { ok: true, reason: '', node: created.node, params: null };
  }, [catalogueItems, createNode, edges, graphWiring, nodes, releaseLatch, setSelectedNode]);

  const linkChoice = useCallback(({ targetNode, inputIndex, targetHandle, sourceNode, field, extraEdges = [] }) => {
    const resolvedTarget = nodes.find(({ id }) => id === targetNode?.id) || targetNode;
    const resolvedHandle = targetHandleFor(resolvedTarget, inputIndex, targetHandle);
    const sourceHandle = field || 'value';
    const nodesSnapshot = nodes.some(({ id }) => id === sourceNode.id) ? nodes : [...nodes, sourceNode];
    const outcome = graphWiring.onConnect({
      source: sourceNode.id,
      sourceHandle,
      target: resolvedTarget.id,
      targetHandle: resolvedHandle,
    }, {
      nodesSnapshot,
      edgesSnapshot: [...edges, ...extraEdges],
    });
    return outcome?.ok
      ? { ok: true, reason: '', params: outcome.params }
      : refusal(outcome?.reason || LINK_REFUSAL_TEXT.operand_link_failed);
  }, [edges, graphWiring, nodes]);

  const pickOperand = useCallback(({ targetNode, inputIndex, targetHandle, choice }) => {
    if (!targetNode || !choice) return refusal(LINK_REFUSAL_TEXT.operand_target_unavailable);
    const resolvedTarget = nodes.find(({ id }) => id === targetNode.id) || targetNode;
    const resolvedHandle = targetHandleFor(resolvedTarget, inputIndex, targetHandle);

    if (choice.kind === 'constant') {
      const next = setOperandConstant({
        inputs: resolvedTarget.data?.inputs || [],
        edges,
        targetNode: resolvedTarget,
        targetId: resolvedTarget.id,
        targetHandle: resolvedHandle,
        inputIndex,
        value: Number(choice.value),
      });
      const nextEdgeIds = new Set(next.edges.map(({ id }) => id));
      const removals = edges
        .filter(({ id }) => !nextEdgeIds.has(id))
        .map(({ id }) => ({ type: 'remove', id }));
      if (removals.length > 0) graphWiring.handleEdgesChange(removals);
      updateNodeData(resolvedTarget.id, { inputs: next.inputs });
      return { ok: true, reason: '', inputs: next.inputs, edges: next.edges };
    }

    if (choice.kind === 'price-field' || choice.kind === 'existing-output') {
      const outcome = linkChoice({
        targetNode: resolvedTarget,
        inputIndex,
        targetHandle: resolvedHandle,
        sourceNode: choice.sourceNode,
        field: choice.field,
        extraEdges: choice.createdEdges,
      });
      if (outcome.ok) clearStagedOperand(choice.sourceNode?.id);
      return outcome;
    }

    if (choice.kind === 'new-indicator') {
      const item = findCatalogueItem(choice.item, catalogueItems);
      if (!item) return refusal(LINK_REFUSAL_TEXT.missing_indicator);
      const provisionalNode = { id: '__provisional', type: item.type, data: item.data };
      const validation = graphWiring.validateConnection({
        source: provisionalNode.id,
        sourceHandle: 'value',
        target: resolvedTarget.id,
        targetHandle: resolvedHandle,
      }, {
        nodesSnapshot: [...nodes, provisionalNode],
        edgesSnapshot: [...edges],
      });
      if (!validation.valid) return refusal(validation.reason);

      const created = createNode(item.type, null, { ...(item.data || {}) }, TREE_AUTHORING_FACTORY_OPTIONS);
      if (!created?.success || !created.node) {
        return refusal(created?.message || LINK_REFUSAL_TEXT.indicator_creation_failed);
      }
      setSelectedNode?.(created.node);
      const outputs = resolveSourceFields(created.node, availableDataFields, allIndicatorMetadata);
      if (outputs.length > 1) {
        const staged = { id: created.node.id, createdEdges: created.createdEdges || [] };
        stagedOperandRef.current = staged;
        setStagedOperand(staged);
        return {
          ok: true,
          reason: '',
          node: created.node,
          createdEdges: created.createdEdges || [],
          pendingOutputs: outputs,
        };
      }
      const outcome = linkChoice({
        targetNode: resolvedTarget,
        inputIndex,
        targetHandle: resolvedHandle,
        sourceNode: created.node,
        field: outputs[0] || 'value',
        extraEdges: created.createdEdges || [],
      });
      if (!outcome.ok) {
        graphWiring.deleteNode(created.node.id, { createdEdges: created.createdEdges || [] });
        return outcome;
      }
      return { ...outcome, node: created.node };
    }

    return refusal(LINK_REFUSAL_TEXT.unsupported_operand);
  }, [allIndicatorMetadata, availableDataFields, catalogueItems, clearStagedOperand, createNode, edges, graphWiring, linkChoice, nodes, setSelectedNode, updateNodeData]);

  const setOperator = useCallback(({ nodeId, op }) => {
    if (!COMPARATOR_OPS[op]) return refusal(LINK_REFUSAL_TEXT.operator_unavailable);
    updateNodeData(nodeId, { operator: op });
    return { ok: true, reason: '' };
  }, [updateNodeData]);

  return {
    createAndAttach,
    pickOperand,
    setOperator,
    operatorOptions,
  };
}
